import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import Icons from '../Icons'
import styled, {ThemeProvider} from 'styled-components'

import merge from 'lodash/merge'

export const SEARCH_INPUT_TYPE = {
  HEADER: 'header',
  CONTENT: 'content'
}

const getTheme = isHeader => {
  const isMobile = window.matchMedia('(max-width:420px)')
  return {
    searchInput: {
      container: {
        height: isHeader
          ? isMobile.matches
            ? '45px'
            : '48px'
          : isMobile.matches
            ? '45px'
            : '60px',
        width: isMobile.matches ? 'calc(100vw - 20px)' : '100%',
        borderRadius: isHeader ? '6px' : '34px',
        backgroundColor: isHeader ? '#32374c' : '#ffffff',
        backgroundColorFocus: isHeader ? '#ffffff' : '#fafbff',
        margin: isHeader ? '0 8px 4px 8px' : '0',
        outlineColorFocus: '3px solid #FF9900;'
      },
      input: {
        fontSize: isHeader
          ? isMobile.matches
            ? '14px'
            : '16px'
          : isMobile.matches
            ? '14px'
            : '18px',
        fontWeight: 'normal',
        letterSpacing: isHeader ? '-0.2px' : '-0.3px',
        color: isHeader ? '#f3f5fb' : '#000000',
        width: isHeader ? '500px' : 'calc(100% - 70px)',
        placeholder: {
        color: isHeader ? '#f3f5fb' : '#3E4044'
        }
      }
    }
  }
}

export const SearchInput = ({
  type,
  placeholder,
  value,
  onSearchChange,
  onCancel,
  autoFocus,
  onKeyDown,
  style = {}
}) => {
  const inputEl = useRef(null)
  const [hasFocus, setHasFocus] = useState(false)
  const onFocus = () => setHasFocus(true)
  const onBlur = () => setHasFocus(false)

  useEffect(
    () => {
      if (autoFocus) {
        setHasFocus(true)
        inputEl.current.focus()
      }
    },
    [autoFocus]
  )

  const isHeader = type === 'header'

  let iconColor = hasFocus ? '#A4A9B7' : '#3E4044'
  let clearIconColor = hasFocus ? '#A4A9B7' : '#3E4044'

  if (isHeader) {
    iconColor = hasFocus ? '#444444' : '#f3f5fb'
    clearIconColor = hasFocus ? '#444444' : '#f3f5fb'
  }

  const theme = merge(getTheme(isHeader), {searchInput: style})

  return (
    <ThemeProvider theme={theme}>
      <StyledContainer hasFocus={hasFocus}>
        <StyledSearchIcon>
          <Icons icon="SEARCH" color={iconColor} />
        </StyledSearchIcon>
        <StyledInput
          id="search-input"
          isHeader={isHeader}
          type={onCancel ? 'text' : 'search'}
          placeholder={placeholder}
          onChange={onSearchChange}
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
          ref={inputEl}
          aria-label="Search"
          role="combobox"
          aria-haspopup="true"
        />
        {onCancel && (
          <StyledCancelIcon hidden={value === ''} onClick={onCancel} onKeyDown={onKeyDown}>
            <Icons icon="CLEAR" color={clearIconColor} width={16} height={16} />
          </StyledCancelIcon>
        )}
      </StyledContainer>
    </ThemeProvider>
  )
}

SearchInput.propTypes = {
  /**
   * type is HEADER or CONTENT
   */
  type: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  onCancel: PropTypes.func,

  /**
   * When true the search input is focused when mounted
   */
  autoFocus: PropTypes.bool.isRequired
}

SearchInput.defaultProps = {
  type: SEARCH_INPUT_TYPE.HEADER,
  placeholder: '',
  autoFocus: false
}

const StyledSearchIcon = styled.div`
  padding-left: 16px;
  padding-right: 6px;
  display: flex;
  align-items: center;

  @media (max-width: 420px) {
    padding-left: 11px;
    padding-right: 0;
  }
`

const StyledCancelIcon = styled.button`
  padding-left: 10px;
  padding-right: 6px;
  -webkit-margin-end: 10px;
  align-items: center;
  cursor: pointer;
  display: ${props => (props.hidden ? 'none' : 'flex')};
  background: none;
  border: none;
  cursor: pointer;
  
  :focus {
	 outline: 3px solid #FF9900;
  }
`

const StyledContainer = styled.div`
  height: ${props => props.theme.searchInput.container.height};
  width: ${props => props.theme.searchInput.container.width};
  border-radius: ${props => props.theme.searchInput.container.borderRadius};
  background-color: ${props =>
    props.hasFocus
      ? props.theme.searchInput.container.backgroundColorFocus
      : props.theme.searchInput.container.backgroundColor};
  color: #8F95AA;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  margin: ${props => props.theme.searchInput.container.margin};
  backdrop-filter: blur(22px);
  border: ${props =>
    props.hasFocus
      ? props.theme.searchInput.container.outlineColorFocus
      : '1px solid #eff0f4'};
`
const StyledInput = styled.input`
  font-family: 'Open Sans';
  font-size: ${props => props.theme.searchInput.input.fontSize};
  font-weight: ${props => props.theme.searchInput.input.fontWeight};
  line-height: 1.79;
  letter-spacing: ${props => props.theme.searchInput.input.letterSpacing};
  color: ${props => props.theme.searchInput.input.color};
  width: ${props => props.theme.searchInput.input.width};
  padding-left: 8px;
  padding-right: 10px;
  background-color: transparent;
  flex: 1;
  border: none;

  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${props => props.theme.searchInput.input.placeholder.color};
  }
  :focus::-webkit-input-placeholder {
    color: #444444;
  }
  :-ms-input-placeholder {
    color: ${props => props.theme.searchInput.input.placeholder.color};
  }
  :focus {
  	color:black;
  }
  '&:focus': {
    outline: none;
  }

  @media (max-width: 1023px) {
    width: 100%;
    height: 36px;
  }
`
